/**
 * MarkAsNcrDialog — opens from the Bookings list row menu.
 * Creates an `lbs_booking_issue` of type=`ncr`, optionally also emails
 * an external party with a public link to /p/ncr/:token.
 */
import { useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Send } from "lucide-react";
import { markBookingAsNcr, notifyNcrExternal } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function MarkAsNcrDialog({ open, onOpenChange, booking, onCreated }) {
  const { user, roles } = useAuth();
  const role = roles?.[0];
  const [title, setTitle]     = useState(`NCR — ${booking?.reference_number || ""}`);
  const [body,  setBody]      = useState("");
  const [externalEmail, setExternalEmail] = useState(booking?.sender_email || "");
  const [externalCompany, setExternalCompany] = useState(booking?.sender_company || booking?.other_party_name || "");
  const [sendEmail, setSendEmail] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!body.trim()) { setErr("Description is required."); return; }
    setErr(null);
    setSaving(true);

    const { data: issue, error } = await markBookingAsNcr({
      bookingId: booking.id,
      title:     title.trim(),
      body:      body.trim(),
      externalEmail:   sendEmail ? externalEmail.trim() : null,
      externalCompany: externalCompany.trim() || null,
    }, user, role);

    if (error) { setSaving(false); setErr(error.message); return; }

    if (sendEmail && externalEmail.trim() && issue) {
      const { error: mailErr } = await notifyNcrExternal(
        issue,
        issue.booking,
        issue.site,
      );
      if (mailErr) {
        toast.error(`NCR raised but email failed: ${mailErr.message}`);
      } else {
        toast.success("NCR raised — external party notified.");
      }
    } else {
      toast.success("NCR raised.");
    }

    setSaving(false);
    onCreated?.(issue);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="mark-ncr-dialog">
        <DialogHeader>
          <DialogTitle>Mark as NCR</DialogTitle>
          <DialogDescription>
            Raise a Non-Conformance Report against booking{" "}
            <strong>{booking?.reference_number}</strong>. Optionally notify the
            external party — they will receive a private link to read the NCR
            and post a resolution without needing an account.
          </DialogDescription>
        </DialogHeader>

        {err && <Alert variant="destructive" data-testid="mark-ncr-error"><AlertDescription>{err}</AlertDescription></Alert>}

        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ncr-title-input" />
          </div>
          <div>
            <Label>Description *</Label>
            <Textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} data-testid="ncr-body-input"
              placeholder="What went wrong? Include details, evidence, expected vs. actual." />
          </div>

          <div className="rounded-md border bg-slate-50 p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <Checkbox checked={sendEmail} onCheckedChange={setSendEmail} data-testid="ncr-send-email" />
              Notify external party by email
            </label>
            {sendEmail && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                <div>
                  <Label className="text-xs">Company</Label>
                  <Input value={externalCompany} onChange={(e) => setExternalCompany(e.target.value)} data-testid="ncr-external-company" />
                </div>
                <div>
                  <Label className="text-xs">Email</Label>
                  <Input type="email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)} data-testid="ncr-external-email" />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ncr-submit">
            <Send className="mr-2 h-4 w-4" /> {saving ? "Saving…" : "Raise NCR"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
